import React, { useState, useCallback, useMemo } from "react";
import "./css/MainCommon.css";
import "./css/LandingPage-Features.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faUserShield,
  faBrain,
  faEnvelope,
  faRobot,
  faShareAlt,
  faHandHoldingHeart,
  faChartLine,
  faExclamationTriangle,
  faBullseye,
  faLock,
  faMobileAlt,
  faFish,
  faNetworkWired,
  faNewspaper,
  faImage,
  faPhone,
  faComments,
  faHeadset,
} from "@fortawesome/free-solid-svg-icons";

// Interactive Feature Card Component with GIF/Text toggle
const InteractiveFeatureCard = ({
  title,
  description,
  features,
  gifUrl,
  gifAlt,
  cardClass,
  icon,
  badge,
  initialShowText = false,
}) => {
  const [showText, setShowText] = useState(initialShowText);

  const handleToggle = useCallback(() => {
    setShowText((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle],
  );

  return (
    <div
      className={`feature-card interactive ${cardClass}`}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-expanded={showText}
      aria-label={`${title}. ${showText ? "Showing details" : "Click to see details"}`}
    >
      {/* Title - Always Visible */}
      <div className="feature-header">
        <FontAwesomeIcon icon={icon} className="feature-icon" />
        <h3 className="feature-title">
          {title}
          {badge && <span className="coming-soon-badge">{badge}</span>}
        </h3>
      </div>

      {/* Content Area - GIF or Text */}
      <div className="feature-content-wrapper">
        {/* GIF Layer */}
        <div className={`feature-gif-layer ${showText ? "hidden" : "visible"}`}>
          {gifUrl ? (
            <img
              src={gifUrl}
              alt={gifAlt || title}
              className="feature-gif"
              loading="lazy"
            />
          ) : (
            <div className="feature-gif-placeholder">
              <FontAwesomeIcon icon={icon} className="placeholder-icon" />
              <span>Animation coming soon</span>
            </div>
          )}
          <div className="gif-overlay-hint">
            <span>Click for details</span>
          </div>
        </div>

        {/* Text Layer */}
        <div
          className={`feature-text-layer ${showText ? "visible" : "hidden"}`}
        >
          {/* <p className="feature-description">{description}</p> */}
          <ul className="feature-list">
            {features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
          <div className="text-overlay-hint">
            <span>Click to see picture</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function LandingPage20({ onManageAccountClick }) {
  // Randomly decide pattern on mount: either odd cards show text or even cards show text
  const showTextOnOddCards = useMemo(() => Math.random() < 0.5, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
  };

  const backgroundStyle = {
    backgroundImage: `url('/DashboardBackground3.png')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    filter: "blur(8px)",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  };

  const overlayStyle = {
    background: "rgba(255, 255, 255, 0.7)",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  };

  // Feature data with GIF paths (replace with actual GIFs)
  const featuresData = [
    {
      title: "Real-time Email & Text Analysis",
      description:
        "Forward suspicious messages to KinShield and get instant AI-powered protection.",
      features: [
        "Emails, text messages & full conversations",
        "Instant scam indicator detection",
        "Detailed risk assessment & safety recommendations",
        "Works with any email or SMS provider",
      ],
      gifUrl: "/Features-Text.png", // Replace with actual GIF path
      gifAlt: "Animation showing suspicious text message being analyzed",
      cardClass: "primary",
      icon: faEnvelope,
    },
    {
      title: "Safe Phishing Simulations",
      description:
        "Build real-world scam recognition skills in a completely safe environment.",
      features: [
        "Tailored to your skill level & preferences",
        "Realistic phishing scenarios via email & text",
        "Protective safety net prevents actual harm",
        "Instant feedback to reinforce learning",
      ],
      gifUrl: "/Features-Training.png",
      gifAlt: "Animation showing safe phishing training simulation",
      cardClass: "secondary",
      icon: faFish,
    },
    {
      title: "Social Media Scam Detection",
      description:
        "Verify suspicious social media offers, claims, and requests before engaging.",
      features: [
        "Works with Facebook, Instagram, X, LinkedIn & more",
        "Analyze posts, ads & sponsored content",
        "Marketplace deals & 'too good to be true' offers",
        "Profile & account authenticity checks",
      ],
      gifUrl: "/Features-SocialMedia.png",
      gifAlt: "Animation showing social media scam being detected",
      cardClass: "tertiary",
      icon: faNetworkWired,
    },
    {
      title: "Advanced Scam Detection Tools",
      description:
        "Comprehensive AI-powered analysis for complex scam scenarios.",
      features: [
        "Screenshot analysis for suspicious alerts & pop-ups",
        "Full conversation analysis for romance & investment fraud",
        "Phone number verification against fraud databases",
        "URL & link safety analysis and recommendations",
      ],
      gifUrl: "/Features-AdvancedTools.png",
      gifAlt: "Animation showing AI analyzing suspicious screenshot",
      cardClass: "quaternary",
      icon: faRobot,
    },
    {
      title: "My Scam Shield™",
      description:
        "Get personalized, real-time actionable guidance to counter ongoing scams and scam attempts.",
      features: [
        "Real-time response strategies for active threats",
        "Step-by-step countermeasures tailored to your situation",
        "Expert recommendations for damage control",
        "More details will be released soon",
      ],
      gifUrl: "/Features-MyScamShield.png",
      gifAlt: "Animation showing real-time scam protection guidance",
      cardClass: "quinary",
      icon: faHeadset,
    },
    {
      title: "Trusted Information & Insights",
      description:
        "Stay ahead of evolving threats with curated insights from authoritative sources.",
      features: [
        "Federal Trade Commission fraud alerts",
        "FBI cybercrime warnings & trends",
        "Better Business Bureau (BBB) scam tracker",
        "Monthly scam tactics & prevention guides",
        "Trainings with quizzes to reinforce learning",
      ],
      gifUrl: "/Features-InfoSources.png",
      gifAlt: "Animation showing trusted fraud alert sources",
      cardClass: "senary",
      icon: faNewspaper,
    },
  ];

  return (
    <main className="landing-main20">
      <div style={backgroundStyle}></div>
      <div style={overlayStyle}></div>
      <div className="scroll-to-top-link" onClick={scrollToTop}>
        Top ↑
      </div>
      <div
        className="landing-page20"
        style={{ position: "relative", zIndex: 2 }}
      >
        {/* Core Features */}
        <div className="features-section">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faShieldAlt} className="hero-icon" />
            KinShield™ Advanced Protection for Everyone
          </h2>
          <p className="section-subtitle">
            Comprehensive AI-powered protection against scams through real-time
            analysis, safe simulations, social media verification, and trusted
            insights - keeping you and your family safe from evolving digital
            threats.
          </p>

          {/* In the InteractiveFeatureCard below, we added useMemo to the imports and create a random 
              pattern that's determined once on component load:
              On each page load, the component randomly selects one of two patterns:
                Pattern 1 (50% chance): Cards 1, 3, 5 show text; Cards 2, 4, 6 show pictures
                Pattern 2 (50% chance): Cards 1, 3, 5 show pictures; Cards 2, 4, 6 show text
              The useMemo with an empty dependency array ensures the random choice is made once when the 
              component mounts and stays consistent during the page session. Refreshing the page gives a 
              new random selection.
           */}
          <div className="features-grid">
            {featuresData.map((feature, index) => (
              <InteractiveFeatureCard
                key={index}
                title={feature.title}
                description={feature.description}
                features={feature.features}
                gifUrl={feature.gifUrl}
                gifAlt={feature.gifAlt}
                cardClass={feature.cardClass}
                icon={feature.icon}
                badge={feature.badge}
                initialShowText={
                  showTextOnOddCards ? index % 2 === 0 : index % 2 === 1
                }
              />
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="cta-section">
          <h2 className="features-cta-title">Don't Wait Until It's Too Late</h2>
          <p className="cta-description">
            Every day you delay protection, scammers get more sophisticated.
            Start building your defense with AI-powered protection today.
          </p>
          <div className="cta-buttons">
            <button
              className="cta-button primary"
              onClick={onManageAccountClick}
            >
              <FontAwesomeIcon icon={faUserShield} />
              Protect Yourself
            </button>
            <button
              className="cta-button secondary"
              onClick={onManageAccountClick}
            >
              <FontAwesomeIcon icon={faHandHoldingHeart} />
              Protect a Loved One
            </button>
          </div>
          <div className="cta-guarantee">
            <FontAwesomeIcon icon={faLock} />
            <span>30-day free trial period • Trusted AI protection</span>
          </div>
        </div>
      </div>
    </main>
  );
}

export default LandingPage20;
